import React, { useEffect, useMemo, useState } from "react";
import { Button as DxButton } from "devextreme-react";
import TreeList, {
  Column,
  FilterRow,
  HeaderFilter,
  Selection
} from "devextreme-react/tree-list";
import FolderIcon from "@mui/icons-material/Folder";
import { useRecoilValue } from "recoil";
import { CircularProgress } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { areArraysEqual, convertFormatDate, convertUTCTimeToLocal, formatMembersCount } from "../../util/Utils";
import { buttonDisabledSelector, entitiesSelector, folderPermissionSnapshotSelector, snapshotEntitiesSelector, userNameByIdSelector, usersPerSiteSelector } from "../../recoil/selectors/legalEntities.selectors";
import { Entity, FolderNode, UserPerSite } from "../../api-client-nswag/custompermission-client";
import { DialogBox } from "../CreateSites/DialogBox";
import UserCustomPermissionsManager from "./UserCustomPermissionsManager";
import { useUsersActions } from "../../recoil/actions/users.actions";
import { isUserListDirtySelector, usersInEntitySelector } from "../../recoil/selectors/users.selectors";
import { useFolderTreeActions } from "../../recoil/actions/folderTree.actions";
import { folderTreeSelector } from "../../recoil/selectors/folderTree.selectors";
import { exportFolderPermissionsToExcel } from "./exportFolderPermissionsToExcel";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import { FormatDate } from "../../api-client-nswag/taxportal-client";
import ActionIconButton from "../../cui/commonComponents/ActionIconButton";
import { FolderLifecycleStatus, resolveFolderLifecycleStatus } from "./manageCustomPermissionsGridHelpers";

interface UserCustomPermissionsManagerProps {
  isSiteLevelFolderPermission: boolean;
  selectedLegalEntities?: Entity | undefined;
  setSelectedLegalEntities?: React.Dispatch<React.SetStateAction<Entity | undefined>>;
  siteId?: string | number;
  onRefreshRequest?: (ctx: { isSiteLevelFolderPermission: boolean; entityId?: number }) => Promise<void> | void;
  isRefreshing?: boolean;
  isCsFlow?: boolean;
}

export const folderPermissionActionOptions = [
  {
    label: "Edit Users",
    value: "edit-users",
  }
];


const CustomFolderPermission: React.FC<UserCustomPermissionsManagerProps> = ({
  isSiteLevelFolderPermission,
  selectedLegalEntities,
  setSelectedLegalEntities,
  siteId,
  onRefreshRequest,
  isRefreshing,
  isCsFlow
}) => {

  const { showSnackbar } = useSnackbar();

  const folderTree = useRecoilValue(folderTreeSelector)
  const isButtonDisabled = useRecoilValue(buttonDisabledSelector)
  const folderPermissionSnapshot = useRecoilValue(folderPermissionSnapshotSelector)
  const snapshotEntities = useRecoilValue(snapshotEntitiesSelector)
  const { loadUsersInEntity, loadUsersPerSite } = useUsersActions()
  const { updateFolderNode, setFolderTreeToAllUsers, revalidateFolderIsRestrictedByParent } = useFolderTreeActions()
  const users = useRecoilValue(usersPerSiteSelector)
  const entities = useRecoilValue(entitiesSelector)
  const usersInEntitySelectorValues = useRecoilValue(usersInEntitySelector)
  const [showUserModel, setShowUserModel] = useState(false);
  const [selectedFolderItem, setSelectedFolderItem] = useState<FolderNode>();
  const isUserlistDirty = useRecoilValue(isUserListDirtySelector);

  useEffect(() => {
    if (!isSiteLevelFolderPermission) return;
    if (!Array.isArray(folderTree) || folderTree.length === 0) return;

    const stack: Array<FolderNode> = folderTree.flatMap((root) => root?.children ?? []);
    let needsRevalidate = false;

    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      if (node.isRestricted === undefined || node.isRestricted === null) {
        needsRevalidate = true;
        break;
      }
      if (Array.isArray(node.children) && node.children.length) {
        stack.push(...node.children);
      }
    }

    if (needsRevalidate) {
      revalidateFolderIsRestrictedByParent({ targetLevel: 1 });
    }
  }, [folderTree, isSiteLevelFolderPermission, revalidateFolderIsRestrictedByParent]);

  const topFolderIds = useMemo(
    () => (Array.isArray(folderTree) ? folderTree.map(n => n.folderId) : []),
    [folderTree]
  );

  const isFolderPermissionDisabled = (data: any): boolean => {
    if (!data) {
      return false;
    }

    const isTopFolder = topFolderIds.includes(data.folderId as number);
    const isRestrictedChild =
      topFolderIds.includes(
        data.parentFolderId as number
      ) &&
      data.entityId !== 0 &&
      isSiteLevelFolderPermission;

    return (
      data.isCountryGroup === true ||
      isTopFolder ||
      isRestrictedChild
    );
  };

  const fetchUsersForLegalEntity = (entity?: FolderNode) => {
    if (!entity) return

    if (!selectedLegalEntities?.isRestricted && !isSiteLevelFolderPermission) {
      loadUsersInEntity(users)
    } else {
      const effectiveUsers = currentUsersMap.get(entity.folderId) ?? (entity?.users || []).map(String)
      const entityUserIds = new Set(effectiveUsers)
      const usersInEntity = users.filter(u => entityUserIds.has(String(u.userId)))
      const usersNotInEntity = users.filter(u => !entityUserIds.has(String(u.userId)))
      loadUsersInEntity(usersInEntity)
      loadUsersPerSite(usersNotInEntity)
    }
  }

  const SingleUserName: React.FC<{ userId: string }> = ({ userId }) => {
    const name = useRecoilValue(userNameByIdSelector(userId));
    return <>{name ?? ""}</>;
  };

  const snapshotTree = useMemo(() => {
    if (isSiteLevelFolderPermission) {
      return Array.isArray(folderPermissionSnapshot) ? folderPermissionSnapshot : [];
    }

    const entityId = selectedLegalEntities?.entityId;
    if (entityId == null || !Array.isArray(snapshotEntities)) return [];

    const snap = snapshotEntities.find((e: Entity) => e?.entityId === entityId);
    const tree = snap?.folderTree;
    return Array.isArray(tree) ? tree : [];
  }, [folderPermissionSnapshot, isSiteLevelFolderPermission, selectedLegalEntities?.entityId, snapshotEntities]);

  const baselineUserIds = useMemo(() => {
    const allSiteUserIds = (users ?? []).map((u) => String(u.userId));
    const siteUserIdSet = new Set(allSiteUserIds);

    if (isSiteLevelFolderPermission) return allSiteUserIds;
    if (!selectedLegalEntities) return allSiteUserIds;

    return selectedLegalEntities.isRestricted
      ? (selectedLegalEntities.userList ?? []).map(String).filter((id) => siteUserIdSet.has(String(id)))
      : allSiteUserIds;
  }, [isSiteLevelFolderPermission, selectedLegalEntities, users]);

  const buildEffectiveUsersMap = (tree: any[]) => {
    const map = new Map<number, string[]>();
    const stack = Array.isArray(tree) ? [...tree] : [];
    const siteUserIdSet = new Set(baselineUserIds.map(String));

    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;

      const folderId = Number(node.folderId);
      const rawUsers = Array.isArray(node.users)
        ? node.users.map(String).filter((id: string) => siteUserIdSet.has(String(id)))
        : [];
      const isRestricted = node?.isRestricted === true;
      const effectiveUsers = !isRestricted && rawUsers.length === 0 ? baselineUserIds : rawUsers;

      if (Number.isFinite(folderId)) {
        map.set(folderId, effectiveUsers);
      }

      if (Array.isArray(node.children) && node.children.length) {
        stack.push(...node.children);
      }
    }

    return map;
  };

  const currentUsersMap = useMemo(
    () => buildEffectiveUsersMap(Array.isArray(folderTree) ? folderTree : []),
    [baselineUserIds, folderTree]
  );

  const snapshotUsersMap = useMemo(
    () => buildEffectiveUsersMap(snapshotTree),
    [baselineUserIds, snapshotTree]
  );

  const snapshotRawUsersMap = useMemo(() => {
    const map = new Map<number, string[]>();
    const stack = [...snapshotTree];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      map.set(Number(node.folderId), (node.users ?? []).map(String));
      if (Array.isArray(node.children)) stack.push(...node.children);
    }
    return map;
  }, [snapshotTree]);

  const snapshotRestrictionsMap = useMemo(() => {
    const map = new Map<number, boolean>();
    const stack = [...snapshotTree];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      map.set(Number(node.folderId), node.isRestricted === true);
      if (Array.isArray(node.children)) stack.push(...node.children);
    }
    return map;
  }, [snapshotTree]);

  const getDirtyFolderUsers = () => {
    const dirty = new Map<number, string[]>();
    const stack: FolderNode[] = Array.isArray(folderTree) ? [...folderTree] : [];

    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;

      const folderId = Number(node.folderId);
      const snapUsers = snapshotUsersMap.get(folderId);
      if (snapUsers) {
        const currUsers = (currentUsersMap.get(folderId) ?? []).map(String);
        const prevUsers = (snapUsers ?? []).map(String);
        if (!areArraysEqual(currUsers, prevUsers)) {
          dirty.set(folderId, currUsers);
        }
      }

      if (Array.isArray(node.children) && node.children.length) {
        stack.push(...node.children);
      }
    }

    return dirty;
  };

  const isFolderDirty = (node: FolderNode): boolean => {
    const snapUsers = snapshotUsersMap.get(node.folderId);
    if (!snapUsers) return false;

    if ((node.isRestricted === true) !== snapshotRestrictionsMap.get(node.folderId)) return true;

    const currUsers = currentUsersMap.get(node.folderId) ?? [];
    return !areArraysEqual(currUsers.map(String), snapUsers.map(String));
  };

  const isFolderStatusDirty = (node: FolderNode): boolean => {
    if (!isFolderDirty(node)) return false;

    const snapshotUsers = snapshotRawUsersMap.get(node.folderId);
    const hasDirectUserChanges =
      snapshotUsers !== undefined &&
      !areArraysEqual((node.users ?? []).map(String), snapshotUsers);
    const hasNewRestriction = node.isRestricted === true &&
      snapshotRestrictionsMap.get(node.folderId) !== true;
    const hasModifiedRestrictedChild = (node.children ?? []).some(
      (child) => child.isRestricted === true && isFolderDirty(child)
    );

    return hasDirectUserChanges || !(hasNewRestriction && hasModifiedRestrictedChild);
  };

  const renderStatusChip = (status: FolderLifecycleStatus) => {
    const cls =
      status === "Modified" ? "status-pill status-pill--modified" :
        status === "Saved" ? "status-pill status-pill--saved" :
          status === "Scheduled" ? "status-pill status-pill--scheduled" :
            status === "Processing" ? "status-pill status-pill--processing" :
              status === "Not Restricted" ? "status-pill status-pill--default" :
              "status-pill status-pill--complete";

    const tooltipMessage =
      status === "Not Restricted" ? "No restrictions are applied." :
        status === "Modified" ? "Changes have been made but not saved." :
          status === "Saved" ? "Changes are saved but not scheduled." :
            status === "Scheduled" ? "Changes are scheduled for publication." :
              status === "Processing" ? "Publication is currently being processed." :
                "Publication is complete.";

    return <span className={cls} title={tooltipMessage}>{status}</span>;
  };

  const cellFolderStatusRender = (cellData: any) => {
    const node = cellData?.data as FolderNode;
    if (!node) return null;
    const status = resolveFolderLifecycleStatus(
      node,
      isFolderStatusDirty(node),
      snapshotRestrictionsMap.get(node.folderId) === true
    );
    return renderStatusChip(status);
  };

  const cellRenderForDate = (cellData: any) => {
    const rawDate = cellData?.value ?? cellData?.data?.dateModified;
    if (!rawDate) return <div />;

    return <div>{convertFormatDate(rawDate, FormatDate.DD)}</div>;
  };

  const cellMembersRender = ({ data }: any) => {
    const isDisabled = isFolderPermissionDisabled(data);
    const isRestricted = data?.isRestricted === true;
    const effectiveUsers = currentUsersMap.get(Number(data?.folderId)) ?? [];
    const usersCount = effectiveUsers.length;
    const showAll = !isRestricted;

    const handleClick = () => {
      if (isDisabled || isRefreshing) {
        return;
      }
      handleUsersClick(data);
    };

    return (
      <span
        className={isDisabled || isRefreshing ? "disabled-link" : "hyper-link-blue"}
        onClick={handleClick}
        role="button"
      >
        {showAll ? (
          "All"
        ) : usersCount === 1 ? (
          <SingleUserName userId={String(effectiveUsers?.[0] ?? "")} />
        ) : (
          formatMembersCount(usersCount)
        )}
      </span>
    );

  };

  const cellFolderRender = (cellData: any) => {
    const { data } = cellData;

    return (
      <div className="cfp-folder-cell">
        <FolderIcon className="cfp-folder-icon" />
        <span>{data?.folderName}</span>
      </div>
    );
  };

  const cellIsRestrictedRender = (cellData: any) => {
    const { data } = cellData;
    const isHide = topFolderIds.includes(data?.folderId as number) && isSiteLevelFolderPermission;

    const isRestricted = data?.isRestricted === true;

    if (isHide) return null;

    return (
      <input type="checkbox" readOnly checked={isRestricted} />
    );
  };

  const cellRenderScheduledDate = (e: any) => {
    const node = e.data as FolderNode;
    if (!node) return null;
    const raw = node.scheduledPublishDate;
    if (!raw) return null;
    return <span className="cell-text">{convertUTCTimeToLocal(raw, FormatDate.DD)}</span>;
  };

  const cellRenderPublishedDate = (e: any) => {
    const node = e.data as FolderNode;
    if (!node) return null;

    const raw = node.publishedDate;
    if (!raw) return null;
    return <span className="cell-text">{convertUTCTimeToLocal(raw, FormatDate.DD)}</span>;
  };

  const handleUsersClick = (data: any) => {
    if (isRefreshing) return;
    if (isSiteLevelFolderPermission && setSelectedLegalEntities) {
      if (data?.entityId === 0) {
        setSelectedLegalEntities((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            entityName: data?.folderName,
          };
        })
      } else {
        const entity = entities.find(e => e.entityId === data?.entityId);
        if (entity) {
          setSelectedLegalEntities(entity)
        }
      }
    }
    const effectiveUsers = currentUsersMap.get(Number(data?.folderId)) ?? [];
    const nextSelected = {
      ...data,
      users: effectiveUsers,
      usersCount: effectiveUsers.length,
    };
    setSelectedFolderItem(nextSelected)
    fetchUsersForLegalEntity(nextSelected)
    setShowUserModel(true);
  }

  const handleUsersModalClose = () => {
    setShowUserModel(false);
  }

  const handleUsersChanges = () => {
    const updatedUsers = usersInEntitySelectorValues.map((u: any) => String(u.userId));
    if (selectedLegalEntities?.entityId && setSelectedLegalEntities && !selectedLegalEntities?.isRestricted && updatedUsers?.length !== users?.length && !isSiteLevelFolderPermission) {
      let usersIds = users.map((u: UserPerSite) => String(u.userId))
      setSelectedLegalEntities((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          isRestricted: true,
        };
      });
      setFolderTreeToAllUsers(usersIds);
    }

    const applyUsersToSubtree = (node: FolderNode, users: string[]): FolderNode => ({
      ...node,
      users: [...users],
      usersCount: users.length,
      children: (node.children ?? []).map(child => applyUsersToSubtree(child, users)),
    });

    setSelectedFolderItem((prev: FolderNode | undefined) => {
      if (!prev) return prev;
      return applyUsersToSubtree(prev, updatedUsers);
    });

    updateFolderNode(selectedFolderItem?.folderId ?? 0, updatedUsers)
    revalidateFolderIsRestrictedByParent(isSiteLevelFolderPermission ? { targetLevel: 1 } : { targetLevel: 0 })
    setShowUserModel(false);
  }

  const userModelTitle = `${selectedFolderItem?.entityId !== 0 ? `${selectedLegalEntities?.entityName} - ` : ""}${selectedFolderItem?.folderName} > Users`;

  const handleExportToExcel = async () => {
    try {
      const effectiveSiteId =
        siteId ?? sessionStorage.getItem("siteId") ?? undefined;
      await exportFolderPermissionsToExcel(folderTree ?? [], users ?? [], effectiveSiteId, isCsFlow);
      showSnackbar({ message: SnackbarMessage.EXPORT_TO_EXCEL_SUCCESS, severity: "success" });
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.EXPORT_TO_EXCEL_FAIL, severity: "error" });
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;

    const dirtyBeforeRefresh = getDirtyFolderUsers();

    await onRefreshRequest?.({
      isSiteLevelFolderPermission,
      entityId: selectedLegalEntities?.entityId,
    });

    if (dirtyBeforeRefresh.size > 0) {
      window.setTimeout(() => {
        dirtyBeforeRefresh.forEach((users, folderId) => {
          updateFolderNode(folderId, users);
        });

        revalidateFolderIsRestrictedByParent(
          isSiteLevelFolderPermission ? { targetLevel: 1 } : { targetLevel: 0 }
        );
      }, 0);
    }
  };

  const cellRenderActions = (e: any) => {
    const rowData = e.data;
    const options = folderPermissionActionOptions.map((opt) => ({
      ...opt,
      onClick: () => {
        handleUsersClick(rowData);
      },
    }));
    const isDisabled = isFolderPermissionDisabled(rowData);

    return (
      <ActionIconButton
        icon={<MoreHorizIcon />}
        title="See Details"
        options={options}
        className="dx-datagrid mcp-action-menu-button"
        disabled={isDisabled}
      />
    );
  };

  return (
    <div className="cfp-hq-page">
      <div className="cfp-toolbar">
        <DxButton
          text="Export"
          hint="Export to Excel"
          icon="exportxlsx"
          onClick={handleExportToExcel}
          disabled={isRefreshing || !Array.isArray(folderTree) || folderTree.length === 0}
        />
        <DxButton
          icon="refresh"
          hint="Refresh"
          onClick={handleRefresh}
          disabled={isRefreshing || typeof onRefreshRequest !== "function"}
        />
        {isRefreshing ? <CircularProgress size={18} /> : null}
      </div>
      <div className={isRefreshing ? "cfp-grid-wrap cfp-grid-wrap--refreshing" : "cfp-grid-wrap"}>
        <TreeList
          key={JSON.stringify(folderTree?.map(x => x.folderId))}
          dataSource={folderTree ?? []}
          keyExpr="folderId"
          dataStructure="tree"
          itemsExpr="children"
          showBorders={true}
          autoExpandAll={true}
          allowColumnResizing={true}
          height={`calc(100vh - 240px)`}
          showColumnLines={false}
          showRowLines={true}
        >
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Selection mode="single" />

          <Column
            dataField="folderName"
            caption="Location"
            cellRender={cellFolderRender}
            minWidth={300}
          />

          <Column
            alignment="left"
            dataField="usersCount"
            caption="Users"
            cellRender={cellMembersRender}
            minWidth={150}
          />
          {isSiteLevelFolderPermission && <Column
            alignment="left"
            dataField="isRestricted"
            caption="Restricted"
            cellRender={cellIsRestrictedRender}
            minWidth={120}
          />}
          <Column
            alignment="left"
            dataField="publishStatus"
            caption="Status"
            cellRender={cellFolderStatusRender}
            minWidth={100}
          />
          {isCsFlow && <Column
            cssClass="cell-text"
            alignment="left"
            dataField="scheduledPublishDate"
            dataType="date"
            caption="Scheduled"
            cellRender={cellRenderScheduledDate}
            minWidth={100}
          />}
          {isCsFlow && <Column
            cssClass="cell-text"
            alignment="left"
            dataField="publishedDate"
            dataType="date"
            caption="Published"
            cellRender={cellRenderPublishedDate}
            minWidth={100}
          />}
          {isCsFlow && <Column
            alignment="left"
            dataField="dateModified"
            caption={"Date Modified"}
            cellRender={cellRenderForDate}
            minWidth={140}
          />}
          {isCsFlow && <Column
            alignment="left"
            dataField="lastModifiedBy"
            caption="Last Modified by"
            minWidth={140}
          />}
          {isCsFlow && <Column
            type="buttons"
            caption={"Actions"}
            width={100}
            headerCellRender={() => (
              <span className="cell-text-header">Actions</span>
            )}
            cellRender={cellRenderActions}
          />}
        </TreeList>
      </div>
      <DialogBox
        isDisplayModal={showUserModel}
        handleClose={handleUsersModalClose}
        btnOpenText={"Save"}
        btnEditText={"Back"}
        handleEdit={handleUsersChanges}
        maxWidth={"sm"}
        btnContinueDisable={isButtonDisabled || !isUserlistDirty}
        handleContinue={handleUsersChanges}
        title={userModelTitle}
      >
        <UserCustomPermissionsManager
          selectedLegalEntities={selectedLegalEntities}
          selectedFolderItem={selectedFolderItem}
        />
      </DialogBox>
    </div>
  );
};

export default CustomFolderPermission;
